import { useMutation } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

export interface ICreateCustomSurveyFromTemplateParams {
	templateId: string;
	name: string;
}

export interface ICreateCustomSurveyFromTemplateOptions {
	impersonationContext?: Api.IImpersonationContext;
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (data: Api.ICustomSurvey) => void;
}

/**
 * Create a new CustomSurvey from a survey template.
 *
 * Backend automatically handles:
 * - Clones CustomForm structure with all fields and field types
 * - Generates new IDs for survey and all fields (prevents conflicts)
 * - Copies intro text, confirmation settings, contact collection options
 * - Sets survey status to draft (not published)
 * - Associates survey with current user's account
 *
 * NOT cloned (reset to defaults):
 * - Survey responses/statistics (starts at 0)
 * - Anonymous link (new unique link generated)
 * - Expiration date (user must set if desired)
 * - Notification recipients (empty by default)
 *
 * Returns complete CustomSurvey ready for editing/publishing.
 * Frontend navigates to edit page after success.
 *
 * @param options.impersonationContext - For admin creating survey on behalf of user
 * @param options.onSuccess - Callback with newly created survey (includes ID)
 * @param options.onError - Error handler for API failures
 * @returns React Query mutation result
 */
export const useCreateCustomSurveyFromTemplateMutation = (options: ICreateCustomSurveyFromTemplateOptions = {}) => {
	const { impersonationContext, onError, onSuccess } = options;
	const userSession = useUserSession();

	return useMutation({
		mutationFn: (params: ICreateCustomSurveyFromTemplateParams) => {
			const { templateId, name } = params;
			const request: Api.IUseSurveyTemplateRequest = { name };

			return userSession.webServiceHelper.callAsync<Api.ICustomSurvey>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `survey/custom/cloneSurveyTemplate/${templateId}`,
				}),
				'POST',
				request
			);
		},
		onSuccess: data => {
			// Invalidate custom surveys list to show the newly created survey
			reactQueryClient.invalidateQueries({ queryKey: ['customSurveys'] });
			onSuccess?.(data);
		},
		onError,
	});
};
